import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import { toast } from "components/ui/use-toast";
import { ToastAction } from "components/ui/toast";

import type { AppDispatch } from "@/store";
import { Tags } from "@/enum/tags";
import {
  taxClosingApi,
  useGenerateTaxClosingMutation,
} from "@/services/tax-closing";

import { type TaxClosingErrorKind, parseTaxClosingError } from "./tax-closing-error";
import { TAX_CLOSING_TOASTS } from "../messages";

const MONTH_CONTROL_PATH = "/tax/closing/periods";

const KINDS_THAT_NAVIGATE_TO_MONTH_CONTROL: Record<TaxClosingErrorKind, boolean> = {
  "month-not-released": true,
  "month-not-found": true,
  "month-closed": true,
  "stores-unauthorized": false,
  "active-job-conflict": false,
  validation: false,
  unknown: false,
};

/**
 * Fluxo síncrono — sem polling, sem Bull queue tracking.
 */
export function useTaxClosing() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const [generateExport, { isLoading: isCreating }] =
    useGenerateTaxClosingMutation();

  const onGenerate = useCallback(
    async (stores: number[], month: number, year: number) => {
      try {
        const result = await generateExport({ stores, month, year }).unwrap();

        toast(
          TAX_CLOSING_TOASTS.generateSuccess(
            stores.length,
            result.lojasFaltando?.length ?? 0,
          ),
        );

        dispatch(
          taxClosingApi.util.invalidateTags([Tags.TAX_CLOSING_HISTORY]),
        );

        navigate(`/tax/closing/${result.id}`);
      } catch (err) {
        const parsed = parseTaxClosingError(err);
        const shouldOfferMonthControl =
          KINDS_THAT_NAVIGATE_TO_MONTH_CONTROL[parsed.kind];

        toast({
          ...TAX_CLOSING_TOASTS.generateError(parsed.title, parsed.description),
          action: shouldOfferMonthControl ? (
            <ToastAction
              altText="Ir para Controle de Meses"
              onClick={() => navigate(MONTH_CONTROL_PATH)}
            >
              Liberar mês
            </ToastAction>
          ) : undefined,
        });
      }
    },
    [generateExport, navigate, dispatch],
  );

  return { onGenerate, isCreating };
}
